import { rotationService } from '../rotationService';
import { canvasService } from '../canvasService';
import {
  DrawingMergeSource,
  DrawingSnapshot,
  FileSnapshot,
} from './types';

interface FileWithRotation extends FileSnapshot {
  imageRotation?: number;
}

export class DrawingService {
  resolveDrawingMergeSource(
    file: FileSnapshot,
    drawing: DrawingSnapshot | undefined
  ): DrawingMergeSource | null {
    const localDrawingSource =
      drawing?.drawingImageUrl || file.data?.drawingSrc;
    const remoteDrawingSource =
      file.signedRequest?.drawingSignedRequest ||
      file.signedRequest?.mergedSignedRequest ||
      file.signedRequest?.imageSignedRequest ||
      file.signedRequest?.signedRequest;

    if (localDrawingSource) {
      return {
        primarySource: localDrawingSource,
        primaryApplyRotation: true,
        fallbackSource: remoteDrawingSource,
        fallbackApplyRotation: true,
      };
    }

    if (remoteDrawingSource) {
      return {
        primarySource: remoteDrawingSource,
        primaryApplyRotation: true,
        fallbackApplyRotation: true,
      };
    }

    return null;
  }

  previousImageNotRelevant(
    cleared: boolean | undefined,
    file: FileSnapshot,
    drawing: DrawingSnapshot | undefined
  ): boolean {
    const hasAnyPreviousDrawingSource = Boolean(
      drawing?.drawingImageUrl ||
        file.data?.drawingSrc ||
        file.signedRequest?.drawingSignedRequest ||
        file.signedRequest?.mergedSignedRequest
    );

    return (
      Boolean(cleared) ||
      !hasAnyPreviousDrawingSource ||
      (!file.data?.isDrawn && !drawing?.drawingImageUrl)
    );
  }

  async createDrawingCanvas(
    cleared: boolean | undefined,
    canvas: HTMLCanvasElement,
    file: FileWithRotation,
    drawing: DrawingSnapshot | undefined
  ): Promise<HTMLCanvasElement> {
    if (this.previousImageNotRelevant(cleared, file, drawing)) {
      return canvas;
    }

    const previousDrawingSource = this.resolveDrawingMergeSource(file, drawing);
    if (!previousDrawingSource) {
      return canvas;
    }

    const loadPreviousImage = async (
      source: string,
      shouldApplyRotation: boolean
    ): Promise<HTMLImageElement> => {
      let cleanupRotatedSource: (() => void) | undefined;
      try {
        const resolved = shouldApplyRotation
          ? await rotationService.applyImageRotationToSource(source, file.imageRotation)
          : { source };
        cleanupRotatedSource = resolved.cleanup;
        return await canvasService.loadImageFromSource(resolved.source);
      } finally {
        cleanupRotatedSource?.();
      }
    };

    try {
      const previousImage = await loadPreviousImage(
        previousDrawingSource.primarySource,
        previousDrawingSource.primaryApplyRotation
      );
      return canvasService.createMergedImageCanvas(previousImage, canvas).original;
    } catch (error) {
      if (!previousDrawingSource.fallbackSource) {
        throw error;
      }
      const fallbackImage = await loadPreviousImage(
        previousDrawingSource.fallbackSource,
        previousDrawingSource.fallbackApplyRotation
      );
      return canvasService.createMergedImageCanvas(fallbackImage, canvas).original;
    }
  }
}

export const drawingService = new DrawingService();

export type {
  DrawingMergeSource,
  DrawingSnapshot,
  FileSnapshot,
} from './types';
